/**
 * ProductCombobox - Dropdown com pesquisa para seleccionar produtos
 * 
 * Resolve o problema de performance com 308+ produtos num Select normal
 */

import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Search, Package } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { formatCurrency } from '@/lib/utils';

export function ProductCombobox({ 
  products = [], 
  value, 
  onValueChange, 
  placeholder = "Seleccionar produto...",
  disabled = false 
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Find selected product
  const selectedProduct = useMemo(() => {
    return products.find(p => p.id === value);
  }, [products, value]);

  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products.slice(0, 50); // Show first 50 by default
    
    const term = searchTerm.toLowerCase();
    return products.filter(p => 
      p.name?.toLowerCase().includes(term) ||
      p.sku?.toLowerCase().includes(term) ||
      p.brand?.toLowerCase().includes(term)
    ).slice(0, 50); // Limit to 50 results
  }, [products, searchTerm]);

  const handleSelect = (productId) => {
    onValueChange(productId);
    setOpen(false);
    setSearchTerm('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
          data-testid="product-combobox-trigger"
        >
          {selectedProduct ? (
            <span className="truncate">
              {selectedProduct.name}
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              placeholder="Pesquisar por nome, SKU ou marca..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
              data-testid="product-search-input"
            />
          </div>
          <CommandList className="max-h-[300px]">
            <CommandEmpty>Nenhum produto encontrado.</CommandEmpty>
            <CommandGroup>
              {filteredProducts.map((product) => {
                // Extract basic specs for display
                const specs = product.specs || {};
                const specsSummary = [];
                
                // CPU
                if (specs.cpu?.modelo) specsSummary.push(specs.cpu.modelo);
                // RAM
                if (specs.memoria?.capacidade) specsSummary.push(specs.memoria.capacidade);
                // Storage
                if (specs.armazenamento?.[0]?.capacidade) specsSummary.push(specs.armazenamento[0].capacidade);
                // Screen
                if (specs.ecra?.polegadas) specsSummary.push(specs.ecra.polegadas);
                
                return (
                  <CommandItem
                    key={product.id}
                    value={product.id}
                    onSelect={() => handleSelect(product.id)}
                    className="cursor-pointer"
                    data-testid={`product-option-${product.sku}`}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 flex-shrink-0",
                        value === product.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="font-medium truncate">{product.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {product.sku} • {product.brand || 'Sem marca'} • {formatCurrency(product.unit_price)}
                      </span>
                      {specsSummary.length > 0 && (
                        <span className="text-xs text-blue-600 font-medium">
                          {specsSummary.join(' | ')}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {filteredProducts.length === 50 && (
              <div className="p-2 text-center text-xs text-muted-foreground border-t">
                Mostrando 50 resultados. Digite para refinar a pesquisa.
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default ProductCombobox;
